import { FunctionalityEnum, UserRoleEnum } from '@elearning-platform/api-types/enums';
import {
  Color,
  Footer,
  IconName,
  SideMenuVariant,
  TopMenuVariant,
  UserMenuData,
  UserMenuOption,
  UserRoleData,
  UseState,
} from '@elearning-platform/ui';
import React, { Dispatch, FC, ReactElement, useEffect, useMemo, useState } from 'react';
import { TransProps, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Location, useLocation } from 'react-router';
import { AnyAction } from 'redux';

import { AppConfig, Theme } from '../../appConfig';
import { AppSettingsActions } from '../../appSettings/redux/appSettings.reducer';
import { selectAppSettingsBrands, selectCurrentBrand } from '../../appSettings/redux/appSettings.selector';
import { ComparisonTablePopup } from '../../components';
import { FavouriteToast } from '../../components/favouritesList/favouriteToast/favouriteToast.component';
import { SearchPopup } from '../../components/searchPopup/searchPopup.component';
import { RouteNameEnum, SearchPopupPhase, ViewImage } from '../../enums';
import { useAuth, useMenuItems, useRedirects, useVolatileSettings } from '../../hooks';
import { LangRouteInfo, UseAuth, UseMenuItems, UseVolatileSettings } from '../../interfaces';
import { ActionRequiredToAccessPlatformView } from '../../pages';
import { selectUnreadNotifications } from '../../redux';
import { UserRoleName } from '../../userContext/userContext.constants';
import { getRouteNameByUrl, getUrlByRouteName } from '../../utils';
import { AgreementsModal } from '../agreementsModal/agreementsModal.component';
import { FooterLinks } from '../app.constants';
import { SearchPopupWrapper } from '../app.styled';
// <== OLD COMPONENTS: remove after full redesign ==>
import { GamificationBadgeModal } from '../gamificationBadgeModal/gamificationBadgeModal.component';
import MainFooter from '../mainFooter/mainFooter.component';
import MainHeader from '../mainHeader';
import MainNavigation from '../mainNavigation';
import { MobileNavigationMenu } from '../mobileNavigationMenu/mobileNavigationMenu.component';
import NetworkErrorModal from '../networkErrorModal';
import OfflineToast from '../offlineToast/offlineToast.component';
import { RegulationsModal } from '../regulationsModal/regulationsModal.component';
// <== OLD COMPONENTS ==>

import { Container, MainContent, RemainingPlace, RightWrapper, StyledSideMenu, StyledTopMenu } from './appWrapper.styled';
import { AppWrapperProps } from './appWrapper.types';
import { useAnonymousMode } from './hooks';

export const AppWrapper: FC<AppWrapperProps> = (props: AppWrapperProps): ReactElement => {
  const { children }: AppWrapperProps = props;
  useRedirects();
  const { currentMenuPositionName, mainPositions }: UseMenuItems = useMenuItems();
  const {
    appSettings,
    hasUserRequiredFunctionalities,
    isAuthInitialized,
    isLoggedIn,
    userData,
    changeCurrentMenuRole,
    signOut,
  }: UseAuth = useAuth();
  const dispatch: Dispatch<AnyAction> = useDispatch();
  const [isMobileMenuVisible, setIsMobileMenuVisible]: UseState<boolean> = useState<boolean>(false);
  const { pathname }: Location = useLocation();
  const currentRouteName: RouteNameEnum | undefined = useMemo((): RouteNameEnum | undefined => getRouteNameByUrl(pathname), [pathname]);
  const currentRouteInfo: LangRouteInfo | undefined = useMemo(
    (): LangRouteInfo | undefined => currentRouteName
      ? getUrlByRouteName(currentRouteName)
      : undefined,
    [currentRouteName]
  );
  const isAppInterfaceHidden: boolean = useAnonymousMode();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const errorNetwork: boolean = useSelector((state: any): boolean => state.errorNetwork.errorNetwork);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const currentBrandId: any = useSelector(selectCurrentBrand);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const settingsBrands: any = useSelector(selectAppSettingsBrands);
  const unreadNotifications: number = useSelector(selectUnreadNotifications) as number;
  const { t }: TransProps<never> = useTranslation();
  const { changeComparisonTablePopupVisible, volatileSettings, handleSearchPopupOpen }: UseVolatileSettings = useVolatileSettings();

  const menuRoles: [UserRoleData, UserRoleData] | undefined = useMemo(
    (): [UserRoleData, UserRoleData] | undefined => {
      if (!userData?.role.name || userData?.role.name === UserRoleEnum.USER) {
        return;
      }

      return [
        { label: userData?.role.value, value: userData?.role.name, icon: IconName.AccountSquare, iconBg: Color.Gray600 },
        {
          label: t('competence.app.appWrapper.userRole'),
          value: UserRoleEnum.USER,
          icon: IconName.AccountSquareOutlined,
          iconBg: Color.Brand300,
        },
      ];
    },
    [userData?.role, t]
  );

  const [topMenuVariant, setTopMenuVariant]: UseState<TopMenuVariant> = useState<TopMenuVariant>(
    appSettings?.currentMenuRole === UserRoleEnum.USER ? TopMenuVariant.Light : TopMenuVariant.Dark
  );
  const [sideMenuVariant, setSideMenuVariant]: UseState<SideMenuVariant> = useState<SideMenuVariant>(
    appSettings?.currentMenuRole === UserRoleEnum.USER ? SideMenuVariant.Light : SideMenuVariant.Dark
  );

  useEffect((): () => void => {
    const updateSideMenuVariant: () => void = (): void => {
      const dashboardSidebarBreakpoint: number = 220;
      const scrollPosition: number = window.scrollY;

      if (currentRouteName === RouteNameEnum.Dashboard && scrollPosition <= dashboardSidebarBreakpoint) {
        setSideMenuVariant(SideMenuVariant.TransparentLight);
        setTopMenuVariant(TopMenuVariant.TransparentLight);
      } else {
        setSideMenuVariant(appSettings?.currentMenuRole === UserRoleEnum.USER ? SideMenuVariant.Light : SideMenuVariant.Dark);
        setTopMenuVariant(appSettings?.currentMenuRole === UserRoleEnum.USER ? TopMenuVariant.Light : TopMenuVariant.Dark);
      }
    };
    updateSideMenuVariant();
    window.addEventListener('scroll', updateSideMenuVariant);

    return (): void => {
      window.removeEventListener('scroll', updateSideMenuVariant);
    };
  }, [currentRouteName, appSettings?.currentMenuRole]);

  useEffect(
    (): void => {
      document.title = currentRouteInfo?.title
        ? `${t(currentRouteInfo.title)} | ${AppConfig.platformName}`
        : AppConfig.platformName;
    },
    [currentRouteInfo, t]
  );

  const userMenuOptions: UserMenuOption[] = useMemo(
    (): UserMenuOption[] => {
      const notificationsUrl: string | undefined = getUrlByRouteName(RouteNameEnum.Notifications)?.url;
      const profileDetailsUrl: string | undefined = getUrlByRouteName(RouteNameEnum.ProfileDetails)?.url;

      return [
        ...(
          notificationsUrl
          && hasUserRequiredFunctionalities([FunctionalityEnum.ApiNotificationList, FunctionalityEnum.ApiNotificationRead])
        ) ? [{
              label: t('competence.app.appWrapper.userMenuOptions.notifications'),
              icon: IconName.Notifications,
              internalPath: notificationsUrl,
              notificationsNumber: unreadNotifications,
            }] : [],
        ...profileDetailsUrl ? [{
          label: t('competence.app.appWrapper.userMenuOptions.profile'),
          icon: IconName.User,
          internalPath: profileDetailsUrl,
        }] : [],
        ...userData?.brands && userData.brands.length > 1 ? [{
          label: t('competence.app.appWrapper.userMenuOptions.brandSwitch'),
          icon: IconName.Switch,
          onClick: (): void => dispatch(AppSettingsActions.deselectBrand()),
        }] : [],
        ...Theme.ComparisonTableEnabled ? [{
          label: t('competence.app.topMenu.userMenuOptions.comparisonTable'),
          icon: IconName.CalendarNote,
          onClick: (): void => changeComparisonTablePopupVisible(true),
        }] : [],
      ];
    },
    [hasUserRequiredFunctionalities, t, unreadNotifications, changeComparisonTablePopupVisible, dispatch, userData?.brands]
  );

  const userDetails: UserMenuData = useMemo(
    (): UserMenuData => {
      const activeMenuRole: UserRoleData | undefined = menuRoles?.find(
        (item: UserRoleData): boolean => item.value === appSettings?.currentMenuRole
      ) ?? menuRoles?.[0];

      return {
        userName: `${userData?.name} ${userData?.surname}`,
        userEmail: userData?.email || '',
        userAvatar: userData?.avatar || '',
        options: userMenuOptions,
        selectedRole: activeMenuRole,
        roles: menuRoles,
        onRoleChange: (option: string): void => changeCurrentMenuRole(option as UserRoleEnum),
        onMenuClose: (): void => setIsMobileMenuVisible(false),
      };
    },
    [userData, userMenuOptions, menuRoles, changeCurrentMenuRole, appSettings?.currentMenuRole]
  );

  const isSearchAvailable: boolean = useMemo(
    (): boolean => !isAppInterfaceHidden && isAuthInitialized && hasUserRequiredFunctionalities([FunctionalityEnum.ApiSearch]),
    [isAuthInitialized, isAppInterfaceHidden, hasUserRequiredFunctionalities]
  );

  const signOutAction: () => void = (): void => {
    signOut();
    setIsMobileMenuVisible(false);
  };

  return (
    <Container>
      {!!(!isAppInterfaceHidden && isLoggedIn) && (
        Theme.ModernNavigation ? (
          !!mainPositions.length && (
            <StyledSideMenu
              currentItemName={currentMenuPositionName}
              items={mainPositions}
              logo={[SideMenuVariant.Dark, SideMenuVariant.TransparentDark].includes(sideMenuVariant)
                ? ViewImage.CommonLogoDark
                : ViewImage.CommonLogoLight
              }
              mobileMenuSettings={{
                userMenuData: userDetails,
                isOpen: isMobileMenuVisible,
                onClose: (): void => setIsMobileMenuVisible(false),
                onLogout: signOutAction,
              }}
              variant={sideMenuVariant}
            />
          )
        ) : (
          <MainNavigation
            availableRoutes={userData?.availableRoutes}
            isOnboardingUser={userData?.role.name === UserRoleName.Onboarding}
            menuToggle={(): void => setIsMobileMenuVisible(!isMobileMenuVisible)}
            mobileVisible={isMobileMenuVisible}
            settingsBrands={settingsBrands}
          />
        )
      )}
      <RightWrapper $withLeftSpace={!!(mainPositions.length && isLoggedIn && !isAppInterfaceHidden)}>
        {!!(mainPositions.length && isLoggedIn) && (
          Theme.ModernNavigation ? (
            <StyledTopMenu
              onBurgerMenuClick={!isAppInterfaceHidden
                ? (): void => setIsMobileMenuVisible(!isMobileMenuVisible)
                : undefined
              }
              onLogoutClick={signOutAction}
              onSearchClick={isSearchAvailable
                ? (): void => handleSearchPopupOpen()
                : undefined
              }
              userMenuData={!isAppInterfaceHidden
                ? userDetails
                : undefined
              }
              variant={topMenuVariant}
            />
          ) : (
            <MainHeader
              additionalBrands={userData?.brands}
              currentBrand={currentBrandId}
              menuToggle={(): void => setIsMobileMenuVisible(!isMobileMenuVisible)}
              user={userData}
            />
          )
        )}
        <MainContent>
          {isAppInterfaceHidden ? <ActionRequiredToAccessPlatformView /> : children}
          <RemainingPlace />
        </MainContent>
        {!!(!isAppInterfaceHidden && isLoggedIn) && (
          Theme.ModernFooter ? (
            <Footer links={FooterLinks} name={AppConfig.platformName} />
          ) : (
            <MainFooter />
          )
        )}
      </RightWrapper>
      {!!(!Theme.ModernNavigation && isLoggedIn) && <MobileNavigationMenu />}
      <NetworkErrorModal isOpen={errorNetwork} />
      <OfflineToast />
      <FavouriteToast />
      {(
        !!userData?.requiredAgreement
        && !appSettings?.passwordExpirationInfo?.forceChange
        && !['/regulamin', '/polityka-prywatnosci', '/klauzula-informacyjna', '/regulamin-szkolen'].includes(pathname)
      ) && (
        <RegulationsModal />
      )}
      {(
        !userData?.requiredAgreement
        && !appSettings?.passwordExpirationInfo?.forceChange
        && !!userData?.missingAgreements?.length
      ) && (
        <AgreementsModal />
      )}
      <GamificationBadgeModal />
      {volatileSettings.searchPopupSettings?.phase !== SearchPopupPhase.Closed && (
        <SearchPopupWrapper>
          <SearchPopup />
        </SearchPopupWrapper>
      )}
      <ComparisonTablePopup />
    </Container>
  );
};
